import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
const ROWS = [
  ['CA prévisionnel HT (an 1)', 'ca_ht', eur],
  ['CAPEX total', 'total_capex', eur],
  ['Frais de personnel (an 1)', 'labor_y1', eur],
  ['EBITDA (an 1)', 'ebitda_y1', eur],
  ['Retour sur investissement', 'payback_months', (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mois`)],
];

const combinedPayback = (cols) => {
  const capex = cols.reduce((a, c) => a + (c.ccd_capex || 0), 0);
  const ebitda = cols.reduce((a, c) => a + (c.ebitda_y1 || 0), 0);
  if (!capex) return 0;
  return ebitda > 0 ? (capex / ebitda) * 12 : null;
};

export const LinkedComparison = ({ simId }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api().get(`/simulations/${simId}/linked`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [api, simId]);
  if (!data?.corner) return null;
  const cols = [data.corner, ...data.vitrines];
  const total = Object.fromEntries(ROWS.map(([, k]) => [k, cols.reduce((a, c) => a + (c[k] || 0), 0)]));
  total.payback_months = combinedPayback(cols);

  return (
    <div className="rounded-xl border border-orange-200 bg-white shadow-sm overflow-x-auto" data-testid="linked-comparison">
      <div className="px-4 py-3 border-b border-orange-100 flex items-center gap-2">
        <Link2 className="w-4 h-4 text-orange-600" />
        <h3 className="text-sm font-bold text-slate-800">Corner et vitrine déportée liés</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-500">
            <th className="text-left px-4 py-2 font-medium" />
            {cols.map((c, i) => (
              <th key={c.id} className="text-right px-4 py-2 font-semibold">
                <button type="button" onClick={() => c.id !== simId && navigate(`/simulation/${c.id}`)} data-testid={`linked-col-${c.id}`}
                  className={`text-right ${c.id === simId ? 'text-slate-900' : 'text-orange-700 hover:underline'}`}>
                  <span className="block text-[10px] uppercase tracking-wide text-slate-400">{i === 0 ? 'Corner' : 'Vitrine déportée'}</span>
                  {c.project_name}
                </button>
              </th>
            ))}
            <th className="text-right px-4 py-2 font-bold text-slate-900 bg-orange-50">Total cumulé</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, k, fmt]) => (
            <tr key={k} className="border-t border-slate-100">
              <td className="px-4 py-2 text-slate-600">{label}</td>
              {cols.map(c => <td key={c.id} className="px-4 py-2 text-right tabular-nums">{fmt(c[k])}</td>)}
              <td className="px-4 py-2 text-right tabular-nums font-bold bg-orange-50" data-testid={`linked-total-${k}`}>{fmt(total[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
